import React, { useState } from 'react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const StatCard = ({ icon, label, value, sub, color, bgColor }) => (
  <div className="bg-white rounded-xl shadow-sm p-4 flex flex-col items-start gap-2">
    <div className={`w-10 h-10 ${bgColor} rounded-xl flex items-center justify-center flex-shrink-0`}>
      <Icon name={icon} size={20} className={color} />
    </div>
    <div className="w-full">
      <p className="text-2xl font-bold text-foreground leading-tight">{value}</p>
      <p className="text-xs font-medium text-foreground leading-snug">{label}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{sub}</p>}
    </div>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload?.length) {
    return (
      <div className="bg-white border border-border rounded-lg shadow-lg p-3">
        <p className="text-xs font-semibold text-foreground mb-1">{label}</p>
        {payload?.map((entry, i) => (
          <p key={i} className="text-xs" style={{ color: entry?.color }}>
            {entry?.name}: <span className="font-bold">{entry?.value}{entry?.unit || ''}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const PerformanceAnalytics = ({ analytics }) => {
  const [activeTab, setActiveTab] = useState('overview');

  const {
    completedTrails = 0,
    inProgressTrails = 0,
    notStartedTrails = 0,
    avgQuizScore = 0,
    totalQuizzes = 0,
    totalCorrect = 0,
    learningStreak = 0,
    progressOverTime = [],
    quizPerformanceByTrail = [],
    recentActivity = []
  } = analytics || {};

  const totalTrails = completedTrails + inProgressTrails + notStartedTrails;
  const completionRate = totalTrails > 0 ? Math.round((completedTrails / totalTrails) * 100) : 0;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'progress', label: 'Progress', icon: 'TrendingUp' },
    { id: 'quizzes', label: 'Quizzes', icon: 'Target' },
  ];

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
          <Icon name="BarChart3" size={20} className="text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-foreground">Performance Analytics</h2>
          <p className="text-sm text-muted-foreground">Your learning metrics at a glance</p>
        </div>
      </div>
      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          icon="Trophy"
          label="Trails Completed"
          value={completedTrails}
          sub={`${completionRate}% completion rate`}
          color="text-yellow-500"
          bgColor="bg-yellow-500/10"
        />
        <StatCard
          icon="Target"
          label="Avg Quiz Score"
          value={`${avgQuizScore}%`}
          sub={`${totalCorrect}/${totalQuizzes} correct`}
          color="text-green-500"
          bgColor="bg-green-500/10"
        />
        <StatCard
          icon="Map"
          label="Total Trails"
          value={totalTrails}
          sub={`${inProgressTrails} in progress`}
          color="text-blue-500"
          bgColor="bg-blue-500/10"
        />
        <StatCard
          icon="Flame"
          label="Active Days"
          value={`${learningStreak}`}
          sub="in the last 7 days"
          color="text-orange-500"
          bgColor="bg-orange-500/10"
        />
      </div>
      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="flex border-b border-border">
          {tabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-5 py-3 text-sm font-medium transition-colors ${
                activeTab === tab?.id
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' :'text-muted-foreground hover:text-foreground hover:bg-muted/30'
              }`}
            >
              <Icon name={tab?.icon} size={15} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>

        <div className="p-5">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-5">
              {/* Trail Status Breakdown */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-3">Trail Status Breakdown</h3>
                <div className="space-y-3">
                  {[
                    { label: 'Completed', count: completedTrails, total: totalTrails, color: 'bg-green-500' },
                    { label: 'In Progress', count: inProgressTrails, total: totalTrails, color: 'bg-blue-500' },
                    { label: 'Not Started', count: notStartedTrails, total: totalTrails, color: 'bg-gray-300' },
                  ]?.map(item => (
                    <div key={item?.label}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-muted-foreground">{item?.label}</span>
                        <span className="font-semibold text-foreground">{item?.count}</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className={`${item?.color} h-2 rounded-full transition-all duration-500`}
                          style={{ width: item?.total > 0 ? `${(item?.count / item?.total) * 100}%` : '0%' }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quiz Performance Ring */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-foreground">Overall Quiz Performance</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{totalQuizzes} quizzes attempted</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-blue-600">{avgQuizScore}%</p>
                    <p className="text-xs text-muted-foreground">average score</p>
                  </div>
                </div>
                <div className="mt-3 w-full bg-white/60 rounded-full h-3">
                  <div
                    className="h-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-700"
                    style={{ width: `${avgQuizScore}%` }}
                  />
                </div>
              </div>

              {/* Recent Activity */}
              {recentActivity?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-3">Recent Activity</h3>
                  <div className="space-y-2">
                    {recentActivity?.slice(0, 4)?.map((item, i) => (
                      <div key={i} className="flex items-center space-x-3 py-2 border-b border-border last:border-0">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                          item?.completedAt ? 'bg-green-100' : 'bg-blue-100'
                        }`}>
                          <Icon
                            name={item?.completedAt ? 'CheckCircle2' : 'PlayCircle'}
                            size={14}
                            className={item?.completedAt ? 'text-green-600' : 'text-blue-600'}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-foreground truncate">{item?.trailTitle || 'Learning Trail'}</p>
                          <p className="text-xs text-muted-foreground">{item?.completedAt ? 'Completed' : `${item?.completionPercentage || 0}% done`}</p>
                        </div>
                        <span className="text-xs text-muted-foreground flex-shrink-0">{item?.timeAgo}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Progress Tab */}
          {activeTab === 'progress' && (
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4">Learning Progress Over Time</h3>
              {progressOverTime?.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={progressOverTime} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="progressGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickLine={false} />
                    <YAxis tick={{ fontSize: 11 }} tickLine={false} domain={[0, 100]} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="avgCompletion"
                      name="Avg Completion"
                      unit="%"
                      stroke="#6366f1"
                      strokeWidth={2}
                      fill="url(#progressGrad)"
                      dot={{ r: 3, fill: '#6366f1' }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Icon name="TrendingUp" size={40} className="text-muted-foreground opacity-40 mb-3" />
                  <p className="text-sm text-muted-foreground">Start completing trails to see your progress chart</p>
                </div>
              )}
            </div>
          )}

          {/* Quizzes Tab */}
          {activeTab === 'quizzes' && (
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4">Quiz Scores by Trail</h3>
              {quizPerformanceByTrail?.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={quizPerformanceByTrail} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="trailName" tick={{ fontSize: 10 }} tickLine={false} />
                    <YAxis tick={{ fontSize: 11 }} tickLine={false} domain={[0, 100]} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="score" name="Score" unit="%" fill="#6366f1" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Icon name="Target" size={40} className="text-muted-foreground opacity-40 mb-3" />
                  <p className="text-sm text-muted-foreground">Complete quizzes in trails to see your scores here</p>
                </div>
              )}

              {/* Quiz Summary */}
              <div className="mt-4 grid grid-cols-3 gap-3">
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-green-600">{totalCorrect}</p>
                  <p className="text-xs text-muted-foreground">Correct</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-red-500">{totalQuizzes - totalCorrect}</p>
                  <p className="text-xs text-muted-foreground">Incorrect</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-blue-600">{totalQuizzes}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PerformanceAnalytics;
